import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Check, X, Crown, Sparkles, Award, Download, TrendingUp, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useState, useEffect } from "react";

const ContaflixPro = () => {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayphoneWidget, setShowPayphoneWidget] = useState(false);
  const [promotion, setPromotion] = useState(null);
  const [billingData, setBillingData] = useState({
    ruc_ci: "",
    email: user?.email || "",
    address: "",
    phone: "",
  });

  useEffect(() => {
    axios.get(`${API}/promotion`).then(({ data }) => {
      if (data.is_live) setPromotion(data);
    }).catch(() => {});
  }, [API]);

  const getDiscountedPrice = (basePrice, plan) => {
    if (!promotion) return null;
    const applies = (plan === "monthly" && promotion.applies_to_monthly) ||
                    (plan === "annual" && promotion.applies_to_annual);
    if (!applies) return null;
    const finalPrice = (basePrice * (100 - promotion.discount_percentage) / 100).toFixed(2);
    return { finalPrice, discount: promotion.discount_percentage };
  };

  const openBillingModal = (plan) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setSelectedPlan(plan);
    setBillingData({ ...billingData, email: user?.email || "" });
    setShowBillingModal(true);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!selectedPlan) return;

    if (!billingData.ruc_ci || !billingData.email || !billingData.address || !billingData.phone) {
      toast.error("Por favor completa todos los campos de facturación");
      return;
    }

    setLoading(true);
    try {
      // 1. Preparar transacción en backend
      const prepareRes = await axios.post(`${API}/payments/payphone/prepare`, {
        plan: selectedPlan,
        billing: billingData,
      });
      const cfg = prepareRes.data;

      // 2. Cargar el SDK de PayPhone Cajita si no está
      if (!window.PPaymentButtonBox) {
        await new Promise((resolve, reject) => {
          // CSS
          if (!document.getElementById("payphone-css")) {
            const link = document.createElement("link");
            link.id = "payphone-css";
            link.rel = "stylesheet";
            link.href = `${cfg.payphone_base_url}/api/button/css`;
            document.head.appendChild(link);
          }
          // JS
          if (!document.getElementById("payphone-js")) {
            const script = document.createElement("script");
            script.id = "payphone-js";
            script.src = `${cfg.payphone_base_url}/api/button/js`;
            script.onload = resolve;
            script.onerror = () => reject(new Error("No se pudo cargar PayPhone"));
            document.body.appendChild(script);
          } else {
            resolve();
          }
        });
      }

      // 3. Cerrar modal de billing y mostrar widget
      setShowBillingModal(false);
      setShowPayphoneWidget(true);

      // 4. Esperar a que el div esté en DOM y montar el botón
      setTimeout(() => {
        const widget = window.PPaymentButtonBox;
        if (!widget) {
          toast.error("Error al iniciar PayPhone. Recarga la página.");
          return;
        }
        new widget({
          token: undefined, // se usa via backend, no se expone aquí
          amount: cfg.amount,
          amountWithoutTax: cfg.amountWithoutTax,
          amountWithTax: cfg.amountWithTax,
          tax: cfg.tax,
          service: cfg.service,
          tip: cfg.tip,
          currency: cfg.currency,
          storeId: cfg.storeId,
          reference: cfg.reference,
          clientTransactionId: cfg.clientTransactionId,
          responseUrl: cfg.responseUrl,
        }).render("pp-button");
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar el pago");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    "Acceso a todos los cursos PRO",
    "Certificados ilimitados",
    "Material premium descargable",
    "Actualizaciones mensuales",
    "Plantillas contables listas",
    "Soporte prioritario",
    "Acceso anticipado a nuevos cursos",
    "Comunidad exclusiva",
  ];

  const freeFeatures = [
    "Acceso a cursos gratuitos",
    "Certificados limitados",
    "Material básico",
  ];

  const proFeatures = [
    "Acceso ilimitado a todos los cursos",
    "Certificados ilimitados",
    "Todo el material premium",
    "Actualizaciones exclusivas",
    "Plantillas y recursos",
    "Soporte prioritario 24/7",
    "Acceso anticipado",
    "Comunidad PRO",
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#E50914]/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Crown className="w-12 h-12 text-[#E50914]" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-none flex items-center justify-center gap-3">
            <img
              src="/logo-v4.png"
              srcSet="/logo-v4.png 1x, /logo-2x.png 2x, /logo-hd.png 4x"
              alt="CONTAFLIX"
              className="h-12 md:h-16 w-auto object-contain"
              style={{ imageRendering: 'auto' }}
            />
            <span className="text-[#E50914]">PRO</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-8">
            Accede a todo el contenido premium, certificados ilimitados y herramientas
            exclusivas para llevar tu carrera al siguiente nivel
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Elige tu plan
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <Card className="bg-card/50 backdrop-blur-md border-white/10 hover:border-[#E50914]/50 transition-all">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Plan Mensual</CardTitle>
              <CardDescription className="text-white/60">
                Flexibilidad total, cancela cuando quieras
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                {(() => {
                  const d = getDiscountedPrice(10, "monthly");
                  return d ? (
                    <>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-2xl font-bold text-white/40 line-through">$10.00</span>
                        <span className="text-5xl font-black text-[#E50914]">${d.finalPrice}</span>
                        <span className="text-white/60">/mes</span>
                        <span className="bg-[#E50914] text-white text-xs font-black px-2 py-0.5 rounded ml-1">-{d.discount}%</span>
                      </div>
                      <p className="text-xs text-[#E50914] font-bold mt-1 uppercase tracking-wide">⚡ Precio con descuento</p>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-white">$10.00</span>
                      <span className="text-white/60">/mes</span>
                    </div>
                  );
                })()}
              </div>
              <Button
                onClick={() => openBillingModal("monthly")}
                disabled={loading || user?.subscription_status === "pro"}
                className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                data-testid="subscribe-monthly-btn"
              >
                {user?.subscription_status === "pro" ? "Plan Actual" : "Comenzar Ahora"}
              </Button>
              <div className="space-y-3 pt-4">
                {features.slice(0, 4).map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#E50914] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Annual Plan */}
          <Card className="bg-gradient-to-br from-[#E50914]/20 to-card/50 backdrop-blur-md border-[#E50914] relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <div className="bg-[#E50914] text-white text-xs font-bold px-3 py-1 rounded-full">
                AHORRA 17%
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-white text-2xl">Plan Anual</CardTitle>
              <CardDescription className="text-white/60">
                El mejor valor, 2 meses gratis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                {(() => {
                  const d = getDiscountedPrice(100, "annual");
                  return d ? (
                    <>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-2xl font-bold text-white/40 line-through">$100.00</span>
                        <span className="text-5xl font-black text-[#E50914]">${d.finalPrice}</span>
                        <span className="text-white/60">/año</span>
                        <span className="bg-[#E50914] text-white text-xs font-black px-2 py-0.5 rounded ml-1">-{d.discount}%</span>
                      </div>
                      <p className="text-xs text-[#E50914] font-bold mt-1 uppercase tracking-wide">⚡ Precio con descuento</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-black text-white">$100.00</span>
                        <span className="text-white/60">/año</span>
                      </div>
                      <p className="text-sm text-white/60 mt-1">Equivale a $8.33/mes</p>
                    </>
                  );
                })()}
              </div>
              <Button
                onClick={() => openBillingModal("annual")}
                disabled={loading || user?.subscription_status === "pro"}
                className="w-full bg-white text-[#E50914] hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.4)] hover:shadow-[0_0_30px_rgba(255,255,255,0.6)] transition-all duration-300 font-semibold h-12"
                data-testid="subscribe-annual-btn"
              >
                {user?.subscription_status === "pro" ? "Plan Actual" : "Comenzar Ahora"}
              </Button>
              <div className="space-y-3 pt-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Comparación de Planes
        </h2>
        <div className="bg-card/30 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-6 text-white font-semibold">Características</th>
                  <th className="text-center p-6 text-white font-semibold">GRATIS</th>
                  <th className="text-center p-6 text-[#E50914] font-semibold">PRO</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Cursos gratuitos", free: true, pro: true },
                  { feature: "Cursos PRO", free: false, pro: true },
                  { feature: "Certificados", free: false, pro: true },
                  { feature: "Material descargable básico", free: false, pro: true },
                  { feature: "Actualizaciones mensuales", free: false, pro: true },
                  { feature: "Plantillas y recursos (solo plan anual)", free: false, pro: "Solo anual" },
                  { feature: "Soporte prioritario", free: false, pro: true },
                  { feature: "Comunidad exclusiva", free: false, pro: true },
                ].map((item, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-6 text-white/80">{item.feature}</td>
                    <td className="p-6 text-center">
                      {typeof item.free === "boolean" ? (
                        item.free ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-white/30 mx-auto" />
                        )
                      ) : (
                        <span className="text-white/60 text-sm">{item.free}</span>
                      )}
                    </td>
                    <td className="p-6 text-center">
                      {typeof item.pro === "boolean" ? (
                        item.pro ? (
                          <Check className="w-5 h-5 text-[#E50914] mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-white/30 mx-auto" />
                        )
                      ) : (
                        <span className="text-white text-sm font-semibold">{item.pro}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Beneficios PRO
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: Sparkles,
              title: "Contenido Exclusivo",
              description: "Cursos PRO actualizados mensualmente",
            },
            {
              icon: Award,
              title: "Certificados Ilimitados",
              description: "Certifica todas tus habilidades",
            },
            {
              icon: Download,
              title: "Material Premium",
              description: "Plantillas, guías y recursos descargables",
            },
            {
              icon: TrendingUp,
              title: "Impulsa tu Carrera",
              description: "Destaca con habilidades profesionales",
            },
          ].map((benefit, index) => (
            <Card
              key={index}
              className="bg-card/50 backdrop-blur-md border-white/10 hover:border-[#E50914]/50 transition-all"
            >
              <CardContent className="p-6 text-center space-y-4">
                <benefit.icon className="w-12 h-12 text-[#E50914] mx-auto" />
                <h3 className="text-white font-bold text-lg">{benefit.title}</h3>
                <p className="text-white/60 text-sm">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Preguntas Frecuentes
        </h2>
        <div className="space-y-4">
          {[
            {
              q: "¿Puedo cancelar en cualquier momento?",
              a: "Sí, puedes cancelar tu suscripción en cualquier momento desde tu perfil. No hay penalizaciones ni cargos adicionales.",
            },
            {
              q: "¿Qué métodos de pago aceptan?",
              a: "Actualmente aceptamos PayPhone. Próximamente agregaremos más opciones de pago.",
            },
            {
              q: "¿Los certificados tienen validez oficial?",
              a: "Los certificados de ContaFLIX certifican tu finalización del curso. Son útiles para tu portafolio profesional y CV.",
            },
            {
              q: "¿Puedo cambiar de plan mensual a anual?",
              a: "Sí, puedes actualizar tu plan en cualquier momento desde tu perfil y se ajustará el costo proporcionalmente.",
            },
          ].map((faq, index) => (
            <Card key={index} className="bg-card/50 backdrop-blur-md border-white/10">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/70 text-sm">{faq.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Billing Modal */}
      {showBillingModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Datos de Facturación</h2>
              <button 
                onClick={() => setShowBillingModal(false)}
                className="text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-[#E50914]/10 border border-[#E50914]/20 rounded-lg">
              <p className="text-white text-sm">
                <span className="font-semibold">Plan seleccionado:</span>{" "}
                {selectedPlan === "monthly" ? "Mensual ($10.00/mes)" : "Anual ($100.00/año)"}
              </p>
            </div>

            <form onSubmit={handleSubscribe} className="space-y-4">
              <div>
                <Label className="text-white">RUC / Cédula</Label>
                <Input
                  value={billingData.ruc_ci}
                  onChange={(e) => setBillingData({ ...billingData, ruc_ci: e.target.value })}
                  required
                  placeholder="0999999999 o 0999999999001"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="billing-ruc-input"
                />
              </div>

              <div>
                <Label className="text-white">Correo Electrónico</Label>
                <Input
                  type="email"
                  value={billingData.email}
                  onChange={(e) => setBillingData({ ...billingData, email: e.target.value })}
                  required
                  placeholder="tu@correo.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="billing-email-input"
                />
              </div>

              <div>
                <Label className="text-white">Dirección</Label>
                <Input
                  value={billingData.address}
                  onChange={(e) => setBillingData({ ...billingData, address: e.target.value })}
                  required
                  placeholder="Av. Principal 123, Quito"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="billing-address-input"
                />
              </div>

              <div>
                <Label className="text-white">Teléfono</Label>
                <Input
                  value={billingData.phone}
                  onChange={(e) => setBillingData({ ...billingData, phone: e.target.value })}
                  required
                  placeholder="0999999999"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="billing-phone-input"
                />
              </div>

              <div className="pt-4 space-y-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white font-semibold h-12"
                  data-testid="confirm-subscription-btn"
                >
                  {loading ? "Procesando..." : "Continuar al pago"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowBillingModal(false)}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PayPhone Cajita Widget Modal */}
      {showPayphoneWidget && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="payphone-widget-modal">
          <div className="bg-card border border-[#E50914]/40 rounded-lg p-6 max-w-md w-full shadow-[0_0_60px_rgba(229,9,20,0.25)]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Completar pago</h3>
              <button
                onClick={() => setShowPayphoneWidget(false)}
                className="text-white/60 hover:text-white"
                data-testid="close-payphone-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-white/70 text-sm mb-4">
              Plan: <span className="text-[#E50914] font-bold">
                {selectedPlan === "monthly" ? "PRO Mensual - $10.00" : "PRO Anual - $100.00"}
              </span>
            </p>
            <p className="text-white/60 text-xs mb-4">
              Haz clic en el botón PayPhone para pagar de forma segura con tu tarjeta o saldo PayPhone.
            </p>
            {/* Aquí se monta el widget de PayPhone */}
            <div id="pp-button" data-testid="pp-button-container"></div>
            <p className="text-white/40 text-xs mt-6 text-center">
              🔒 Pago seguro procesado por PayPhone
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ContaflixPro;
